/**
 * =============================================================================
 * IDS CRM Web - Pick the robots on a job
 * =============================================================================
 *
 * Several robots on one job (Phase 3): Schedule Engineer Work lets the team
 * leader add every robot the visit covers, the same way EngineerPicker adds
 * the crew - a dropdown of the robots not yet added, an Add button, and the
 * chosen robots listed underneath with a remove button each. The dropdown only
 * offers robots not already on the list, so nothing is added twice.
 *
 * The first robot on the list is the primary robot: it keeps the job's old
 * single asset slot, so every read that still judges a job by one robot (the
 * money view, the maintenance plan) keeps working. The row says "Primary".
 *
 * noteFor (optional, Phase 6 step 3): something shown under a robot, the same
 * pattern as EngineerPicker - Schedule Engineer Work puts the double-booking
 * warnings there. Without it, or when it gives nothing for a robot, the row
 * renders exactly as before.
 * =============================================================================
 */

import React, { useState } from 'react';
import './AssetPicker.css';

/**
 * @param {object} props
 * @param {string} props.id - id for the dropdown, so a <label htmlFor> names it
 * @param {Array<{asset_id: string, asset_ref: string, robot_type?: string, serial_number?: string}>} props.options
 *        the robots that can be added (the chosen site's robots)
 * @param {string[]} props.selectedIds - the robots on the job, in the order added; the first is primary
 * @param {(ids: string[]) => void} props.onChange
 * @param {Object<string, string|{asset_ref: string, robot_type?: string, serial_number?: string}>} [props.knownRefs]
 *        details for listed robots that are not in options (e.g. a robot that has since moved site)
 * @param {string} [props.emptyText]
 * @param {(assetId: string) => React.ReactNode|null} [props.noteFor] - shown under that robot
 */
// Inline so the picker's own stylesheet is unchanged; the same shape as
// EngineerPicker's .engineer-picker-main (name on top, the note under it).
const NOTE_WRAPPER_STYLE = { display: 'flex', flex: 1, flexDirection: 'column', gap: '4px', minWidth: 0, padding: '6px 0' };

function AssetPicker({ id, options, selectedIds, onChange, knownRefs = {}, emptyText = 'No robot added yet.', noteFor }) {
  const [choice, setChoice] = useState('');
  const selected = Array.isArray(selectedIds) ? selectedIds : [];
  const robots = Array.isArray(options) ? options : [];

  const detailsOf = (assetId) => {
    const robot = robots.find(entry => entry.asset_id === assetId);
    if (robot) return robot;
    const known = knownRefs[assetId];
    if (!known) return { asset_id: assetId, asset_ref: 'Unknown robot' };
    if (typeof known === 'string') return { asset_id: assetId, asset_ref: known };
    return { asset_id: assetId, ...known };
  };

  const available = robots.filter(robot => !selected.includes(robot.asset_id));
  // A choice that has since been added, or has gone from the options, cannot be added.
  const choiceValid = available.some(robot => robot.asset_id === choice);

  const add = () => {
    if (!choiceValid) return;
    onChange([...selected, choice]);
    setChoice('');
  };

  const remove = (assetId) => onChange(selected.filter(entry => entry !== assetId));

  return (
    <div className="asset-picker">
      <div className="asset-picker-row">
        <select
          id={id}
          value={choiceValid ? choice : ''}
          onChange={event => setChoice(event.target.value)}
          disabled={available.length === 0}
        >
          <option value="">
            {robots.length === 0
              ? 'No robots to choose from'
              : (available.length === 0 ? 'Every robot has been added' : 'Choose a robot…')}
          </option>
          {available.map(robot => (
            <option key={robot.asset_id} value={robot.asset_id}>
              {robot.asset_ref}{robot.robot_type ? ` - ${robot.robot_type}` : ''}
            </option>
          ))}
        </select>
        <button type="button" className="asset-picker-add" onClick={add} disabled={!choiceValid}>
          Add
        </button>
      </div>

      {selected.length === 0 ? (
        <p className="asset-picker-empty">{emptyText}</p>
      ) : (
        <ul className="asset-picker-list" aria-label="Robots added">
          {selected.map((assetId, index) => {
            const robot = detailsOf(assetId);
            const ref = robot.asset_ref || 'Unknown robot';
            const parts = [ref, robot.robot_type, robot.serial_number].filter(Boolean);
            const note = typeof noteFor === 'function' ? noteFor(assetId) : null;
            const name = (
              <span className="asset-picker-name">
                {parts.join(' · ')}
                {index === 0 && <span className="asset-picker-primary">Primary</span>}
              </span>
            );
            return (
              <li key={assetId} className="asset-picker-item">
                {note ? (
                  <div className="asset-picker-main" style={NOTE_WRAPPER_STYLE}>
                    {name}
                    <div className="asset-picker-note">{note}</div>
                  </div>
                ) : name}
                <button
                  type="button"
                  className="asset-picker-remove"
                  onClick={() => remove(assetId)}
                  aria-label={`Remove ${ref}`}
                  title={`Remove ${ref}`}
                >
                  <span aria-hidden="true">×</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default AssetPicker;
